import { useCallback, useEffect, useMemo, useState } from 'react'
import BN from 'bn.js'
import Decimal from 'decimal.js'
import { PublicKey } from '@solana/web3.js'
import { Raydium, TxVersion, TickUtils, CLMM_PROGRAM_ID } from '@raydium-io/raydium-sdk-v2'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import { MINTS, SYMBOLS } from '../../api/trade'
import { newConnection } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import { useSplBalance } from '../../hooks/useSplBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

type PoolInfo = any
type Position = any

interface PositionRow {
  nftMint: string
  tickLower: number
  tickUpper: number
  priceLower: string
  priceUpper: string
  liquidity: BN
  raw: Position
}

const SOL_MINT = MINTS.SOL
const USDC_MINT = MINTS.USDC

/** Retry an async fn N times with exponential backoff (only on errors that look transient). */
async function retryWithBackoff<T>(fn: () => Promise<T>, attempts = 3, baseDelayMs = 700): Promise<T> {
  let lastErr: unknown
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (e: any) {
      lastErr = e
      const status = e?.response?.status ?? e?.status
      // Retry on 5xx, network errors, axios timeouts. Don't retry 4xx (auth/bad request).
      const isTransient = !status || (status >= 500 && status < 600) ||
        e?.code === 'ECONNABORTED' || e?.code === 'ERR_NETWORK'
      if (!isTransient || i === attempts - 1) throw e
      await new Promise(r => setTimeout(r, baseDelayMs * Math.pow(2, i)))
    }
  }
  throw lastErr
}

/** Friendly message for Raydium-API failures, distinguishing infra hiccups from real bugs. */
function raydiumLoadError(e: any, fallback: (e: unknown) => string): string {
  const status = e?.response?.status ?? e?.status
  if (status >= 500 && status < 600) {
    return `Raydium API is temporarily unavailable (HTTP ${status}). Please try again in a moment.`
  }
  if (e?.code === 'ECONNABORTED' || e?.code === 'ERR_NETWORK' || e?.message?.includes('Network')) {
    return 'Network error reaching Raydium API. Check your connection and retry.'
  }
  return 'Failed to load Raydium pool: ' + fallback(e)
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL ?? ''

/** Fetch Raydium pool list through our backend proxy (cached, retry, stale-on-error). */
async function fetchPoolViaBackend(mint1: string, mint2: string, poolType: string) {
  const token = sessionStorage.getItem('jwt')
  const url = `${API_BASE}/api/raydium/pool?mint1=${encodeURIComponent(mint1)}` +
              `&mint2=${encodeURIComponent(mint2)}&poolType=${encodeURIComponent(poolType)}`
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    // Throw an axios-shaped error so retryWithBackoff + raydiumLoadError can detect it.
    const text = await res.text().catch(() => '')
    const err: any = new Error(`HTTP ${res.status}${text ? ': ' + text.slice(0, 120) : ''}`)
    err.response = { status: res.status }
    err.status = res.status
    throw err
  }
  return res.json()
}

interface Props {
  balances: WalletBalance[] | null
}

export default function RaydiumLpForm({ balances }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()

  const solBalance  = findBalance(balances, MINTS.SOL)    // display only
  const usdcBalance = findBalance(balances, MINTS.USDC)    // display only
  const splSol = useSplBalance(publicKey, MINTS.SOL)        // authoritative wallet SOL via connection.getBalance

  const [raydium, setRaydium]     = useState<Raydium | null>(null)
  const [pool, setPool]           = useState<PoolInfo | null>(null)
  const [positions, setPositions] = useState<PositionRow[]>([])
  const [loading, setLoading]     = useState(false)
  const [result, setResult]       = useState<TradeResult>({ kind: 'idle' })

  const [lowerPct, setLowerPct]   = useState(5)
  const [upperPct, setUpperPct]   = useState(5)
  const [solAmount, setSolAmount] = useState('')
  const [selectedPos, setSelectedPos] = useState<PositionRow | null>(null)
  const [removePct, setRemovePct] = useState(100)

  const currentPrice = pool?.price ?? 0

  const loadSdk = useCallback(async () => {
    const owner = publicKey ?? (portfolioId ? new PublicKey(portfolioId) : null)
    if (!owner) return
    setLoading(true)
    try {
      const connection = newConnection()
      const r = await Raydium.load({
        connection,
        owner,
        disableFeatureCheck: true,
        disableLoadToken: true,
      } as any)
      setRaydium(r)
      // Pool definition comes from our backend proxy at /api/raydium/pool —
      // it caches Raydium api-v3 with a 60s TTL and serves stale-on-error so
      // a Raydium CDN blip doesn't break the slide-in. Same JSON shape as the
      // SDK's fetchPoolByMints would have returned.
      const pools: any = await retryWithBackoff(
        () => fetchPoolViaBackend(SOL_MINT, USDC_MINT, 'concentrated'),
        2,
        500,
      )
      // Raydium api-v3 /pools/info/mint shape: { success, data: { count, data: [...] } }
      // Our backend proxy passes the response through unchanged, so unwrap both layers.
      // Fallbacks cover (a) flattened variants a future backend might emit, (b) a raw array.
      const list = (pools?.data?.data ?? pools?.data ?? pools) as any[]
      const top = Array.isArray(list) ? list.find((p: any) => p.type === 'Concentrated') ?? list[0] : null
      if (top) setPool(top)
      if (top) {
        const ownerPos = await (r.clmm as any).getOwnerPositionInfo({ programId: CLMM_PROGRAM_ID })
        const forPool = ownerPos.filter((p: any) => p.poolId.toBase58() === top.id)
        const rows: PositionRow[] = forPool.map((p: any) => {
          const priceLower = TickUtils.getTickPrice({ poolInfo: top, tick: p.tickLower, baseIn: true }).price
          const priceUpper = TickUtils.getTickPrice({ poolInfo: top, tick: p.tickUpper, baseIn: true }).price
          return {
            nftMint: p.nftMint.toBase58(),
            tickLower: p.tickLower,
            tickUpper: p.tickUpper,
            priceLower: priceLower.toFixed(4),
            priceUpper: priceUpper.toFixed(4),
            liquidity: p.liquidity,
            raw: p,
          }
        })
        setPositions(rows)
      }
    } catch (e) {
      setResult({ kind: 'error', message: raydiumLoadError(e, friendlyTxError) })
    } finally { setLoading(false) }
  }, [portfolioId, publicKey, friendlyTxError])

  useEffect(() => { loadSdk() }, [loadSdk])

  // Derive ticks from % range around current price
  const tickBounds = useMemo(() => {
    if (!pool) return null
    const priceLower = new Decimal(currentPrice).mul(1 - lowerPct / 100)
    const priceUpper = new Decimal(currentPrice).mul(1 + upperPct / 100)
    try {
      const { tick: tickLower } = TickUtils.getPriceAndTick({ poolInfo: pool, price: priceLower, baseIn: true })
      const { tick: tickUpper } = TickUtils.getPriceAndTick({ poolInfo: pool, price: priceUpper, baseIn: true })
      return { tickLower, tickUpper, priceLower: priceLower.toFixed(4), priceUpper: priceUpper.toFixed(4) }
    } catch { return null }
  }, [pool, currentPrice, lowerPct, upperPct])

  // Rough USDC estimate from current price — real on-chain amount is set by
  // the program based on tick range. We overshoot slippage to avoid reverts.
  const estimatedUsdcAmount = useMemo(() => {
    const solBn = parseSol(solAmount)
    if (!solBn || !currentPrice) return null
    const sol = Number(solBn.toString()) / 1e9
    const usdc = sol * Number(currentPrice)
    return Math.floor(usdc * 1e6)
  }, [solAmount, currentPrice])

  const otherAmountMaxBn = useMemo(() => {
    if (estimatedUsdcAmount == null) return null
    return new BN(Math.floor(estimatedUsdcAmount * 1.5))
  }, [estimatedUsdcAmount])

  const addLiquidity = async () => {
    if (!raydium || !pool || !tickBounds) return
    const solBn = parseSol(solAmount)
    if (!solBn || solBn.isZero()) return

    setResult({ kind: 'pending', label: 'Building LP transaction…' })
    try {
      if (!connected || !publicKey) throw new Error('Wallet not connected')
      if (portfolioId && publicKey.toBase58() !== portfolioId) {
        throw new Error('Connected wallet does not match your session')
      }
      const otherMax = otherAmountMaxBn ?? new BN('1000000000000')
      const { tickLower, tickUpper } = tickBounds
      const lo = Math.min(tickLower, tickUpper)
      const hi = Math.max(tickLower, tickUpper)

      const build: any = await (raydium.clmm as any).openPositionFromBase({
        poolInfo: pool,
        tickLower: lo,
        tickUpper: hi,
        base: 'MintA',
        baseAmount: solBn,
        otherAmountMax: otherMax,
        ownerInfo: { useSOLBalance: true },
        txVersion: TxVersion.V0,
      })

      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = build.transaction
      const submitted = await signAndSubmit(tx)
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `Opened LP position: ${solAmount} SOL between ${tickBounds.priceLower} – ${tickBounds.priceUpper} USDC/SOL`,
      })
      setSolAmount('')
      setTimeout(loadSdk, 4000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const removeLiquidity = async () => {
    if (!raydium || !pool || !selectedPos) return
    setResult({ kind: 'pending', label: 'Building remove-liquidity transaction…' })
    try {
      const liquidityToRemove = selectedPos.liquidity.muln(Math.floor(removePct * 100)).divn(10000)
      if (liquidityToRemove.isZero()) {
        setResult({ kind: 'error', message: 'Liquidity to remove is zero' })
        return
      }
      const build: any = await (raydium.clmm as any).decreaseLiquidity({
        poolInfo: pool,
        ownerPosition: selectedPos.raw,
        ownerInfo: { useSOLBalance: true, closePosition: removePct >= 100 },
        liquidity: liquidityToRemove,
        amountMinA: new BN(0),
        amountMinB: new BN(0),
        txVersion: TxVersion.V0,
      })

      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = build.transaction
      const submitted = await signAndSubmit(tx)
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `Removed ${removePct}% of LP position`,
      })
      setSelectedPos(null)
      setTimeout(loadSdk, 4000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-surface-hover p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <TokenIcon symbol="SOL" mint={MINTS.SOL} size={18} />
            <TokenIcon symbol="USDC" mint={MINTS.USDC} size={18} className="-ml-2" />
            <div className="ml-1">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">SOL/USDC Raydium CLMM</div>
              <div className="mt-0.5 text-sm font-semibold text-ink">
                {pool ? `${pool.id.slice(0, 6)}…${pool.id.slice(-4)}` : (loading ? 'Loading…' : 'Not loaded')}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-gray-500">Current price</div>
            <div className="text-sm font-semibold text-ink">
              {currentPrice ? `${Number(currentPrice).toFixed(2)} USDC/SOL` : '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <BalanceRow mint={MINTS.SOL}  balance={solBalance}  />
        <BalanceRow mint={MINTS.USDC} balance={usdcBalance} />
      </div>

      {/* Add liquidity */}
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-ink">Add liquidity / Open position</h3>

        <div className="grid grid-cols-2 gap-2">
          <RangeInput label="Lower (−%)" value={lowerPct} onChange={setLowerPct} />
          <RangeInput label="Upper (+%)" value={upperPct} onChange={setUpperPct} />
        </div>

        {tickBounds && (
          <div className="rounded-lg bg-surface-hover p-2 text-[11px] text-ink-muted">
            Range: <span className="text-ink">{tickBounds.priceLower}</span> → <span className="text-ink">{tickBounds.priceUpper}</span> USDC/SOL
          </div>
        )}

        <div className="rounded-lg border border-fringe bg-surface-hover p-3">
          <label className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-gray-500">
            <span>Deposit SOL</span>
            {!splSol.raw.isZero() && (
              <button
                type="button"
                onClick={() => setSolAmount(Math.max(0, Number(splSol.decimal) - 0.01).toString())}
                className="text-accent-purple hover:underline"
              >MAX {formatBalance(splSol.decimal)}</button>
            )}
          </label>
          <div className="flex items-center gap-2">
            <TokenIcon symbol="SOL" mint={MINTS.SOL} size={18} />
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={solAmount}
              onChange={e => setSolAmount(e.target.value)}
              className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
            />
          </div>
          {estimatedUsdcAmount != null && (
            <div className="mt-1 text-[11px] text-gray-500">
              ≈ <span className="text-ink">{(estimatedUsdcAmount / 1e6).toFixed(2)}</span> USDC at current price (actual amount depends on range)
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={!raydium || !tickBounds || !parseSol(solAmount) || result.kind === 'pending'}
          onClick={addLiquidity}
          className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
        >
          {result.kind === 'pending' ? 'Working…' : connected ? 'Sign & Add Liquidity' : 'Connect & Add Liquidity'}
        </button>
      </div>

      {/* Remove / close */}
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-ink">Your positions ({positions.length})</h3>

        {positions.length === 0 && (
          <div className="rounded-lg bg-surface-hover px-3 py-4 text-center text-[11px] text-gray-500">
            {loading ? 'Loading positions…' : 'No open positions in this pool.'}
          </div>
        )}

        {positions.map(p => (
          <button
            key={p.nftMint}
            onClick={() => setSelectedPos(p)}
            className={`w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors
              ${selectedPos?.nftMint === p.nftMint ? 'border-accent-purple bg-accent-purple/15' : 'border-fringe bg-surface-hover hover:bg-surface-hover'}`}
          >
            <div className="flex items-center justify-between text-ink">
              <span>#{p.nftMint.slice(0, 6)}…{p.nftMint.slice(-4)}</span>
              <span className="text-ink-muted">liq {p.liquidity.toString().slice(0, 8)}</span>
            </div>
            <div className="text-[11px] text-gray-500">
              Range: {p.priceLower} → {p.priceUpper}
            </div>
          </button>
        ))}

        {selectedPos && (
          <>
            <div className="rounded-lg bg-surface-hover p-3">
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-gray-500">Remove %</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={removePct}
                  onChange={e => setRemovePct(Number(e.target.value))}
                  className="flex-1 accent-accent-purple"
                />
                <span className="w-12 text-right text-sm font-semibold text-ink">{removePct}%</span>
              </div>
              <div className="mt-1 flex gap-1">
                {[25, 50, 75, 100].map(v => (
                  <button key={v}
                    onClick={() => setRemovePct(v)}
                    className={`flex-1 rounded-md px-2 py-1 text-[11px] ${removePct === v ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted hover:bg-surface-hover'}`}
                  >{v}%</button>
                ))}
              </div>
            </div>

            <button
              type="button"
              disabled={result.kind === 'pending'}
              onClick={removeLiquidity}
              className="w-full rounded-lg border border-accent-red/50 bg-accent-red/15 py-2.5 text-sm font-semibold text-accent-red hover:bg-accent-red/25 disabled:cursor-not-allowed"
            >
              {result.kind === 'pending' ? 'Working…' : `Sign & Remove ${removePct}% ${removePct >= 100 ? '(Close)' : ''}`}
            </button>
          </>
        )}
      </div>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />
      {result.kind === 'error' && /Raydium API|Network error/.test(result.message) && (
        <button
          type="button"
          onClick={() => { setResult({ kind: 'idle' }); loadSdk() }}
          disabled={loading}
          className="mt-2 w-full rounded-md border border-accent-purple/40 bg-accent-purple/10 px-3 py-1.5 text-xs font-semibold text-accent-purple hover:bg-accent-purple/20 disabled:opacity-50"
        >
          {loading ? 'Retrying…' : 'Retry'}
        </button>
      )}
    </div>
  )
}

function RangeInput({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="rounded-lg bg-surface-hover p-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0.1}
          step={0.1}
          value={value}
          onChange={e => onChange(Number(e.target.value) || 0)}
          className="w-full bg-transparent text-sm font-semibold text-ink outline-none"
        />
        <span className="text-xs text-gray-500">%</span>
      </div>
    </div>
  )
}

function parseSol(raw: string): BN | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '000000000').slice(0, 9)
  try { return new BN((whole || '0') + padded) } catch { return null }
}

function BalanceRow({ mint, balance }: { mint: string; balance: ReturnType<typeof findBalance> }) {
  const sym = SYMBOLS[mint] ?? '?'
  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-hover p-2.5">
      <TokenIcon symbol={sym} mint={mint} size={18} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{sym}</div>
        <div className="truncate text-sm font-semibold text-ink">
          {balance ? formatBalance(balance.amount, sym === 'USDC' ? 2 : 4) : '—'}
        </div>
        <div className="text-[10px] text-gray-500">
          {balance ? formatUsdShort(balance.usdValue) : '—'}
        </div>
      </div>
    </div>
  )
}

